/**
 * MerchFulfillmentWonModal
 *
 * Surfaces in GlobalRewardNotifications when a DistributedReward lands
 * with notificationMetadata.notificationType === MERCH_FULFILLMENT —
 * i.e. a wheel spin / gamification / manual-grant path awarded a
 * shippable reward (MERCH/GIFT_CARD/OTHER) whose RewardConfig has the
 * admin-toggleable `useInlineFulfillmentForm` flag set.
 *
 * Persistence model — two separate close paths:
 *   - `onClose` (header X / backdrop click) hides the modal for THIS
 *     SESSION ONLY. The DistributedReward stays undismissed server-side,
 *     so the modal re-surfaces on the next page load. This prevents
 *     a user who accidentally dismisses (or closes the tab mid-form)
 *     from losing access to their physical-prize claim flow forever.
 *   - `onClaimed` fires only when the embedded RewardFulfillmentClaimForm
 *     successfully POSTs — that's when we mark the DistributedReward
 *     dismissed server-side. The claim row in the DB is the canonical
 *     "user has provided shipping info" record; flipping the
 *     notification dismissed flag pairs with it.
 *
 * The modal shows the reward image (assetId on notificationMetadata) +
 * name, then embeds the platform RewardFulfillmentClaimForm anchored to
 * the DistributedReward._id. The form POSTs to
 * /rewards/fulfillment-claim — server enforces ownership via
 * DistributedReward.userId match and idempotency via the partial unique
 * index on (userId, distributedRewardId).
 */
import { FC, useState } from "react";
import {ModalHeader, ModalBody} from "@/widgets/ModalParts";
import { DistributedRewardResponse } from "@devour/client";
import {Modal} from "@/widgets/Modal";
import AssetImage from "@/components/AssetImage";
import RewardFulfillmentClaimForm from "@/components/rewards/RewardFulfillmentClaimForm";
import { MinelabRttrRulesModal } from "@/pages/hub/custom/minelab-rttr/components/MinelabRttrRulesModal";

interface Props {
    reward: DistributedRewardResponse;
    isOpen: boolean;
    /*
     * Session-only dismiss (header X / backdrop click). Does NOT mark
     * the reward dismissed server-side — the modal re-surfaces on the
     * next page load so the user can still claim later.
     */
    onClose: () => void;
    /*
     * Successful form submission. Fires the server-side dismiss so
     * GlobalRewardNotifications stops surfacing this reward in future
     * sessions.
     */
    onClaimed: () => void;
}

const MerchFulfillmentWonModal: FC<Props> = ({ reward, isOpen, onClose, onClaimed }) => {
    const meta = reward.notificationMetadata;
    const rewardName = meta?.rewardName ?? reward.name ?? "";
    const assetId = meta?.assetId;
    /*
     * MinelabRttrRulesModal is RTTR-specific; the only live campaign
     * producing MERCH_FULFILLMENT rewards today is RTTR, so direct import
     * is acceptable. When a second brand ships physical-reward fulfillment,
     * generalize via a brand-keyed rules registry on the reward's
     * notificationMetadata.
     */
    const [rulesOpen, setRulesOpen] = useState(false);

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            contentClassName="merch-fulfillment-won-modal"
        >
            <ModalHeader toggle={onClose}/>
            <ModalBody className="merch-fulfillment-won-modal_body">
                <div className="merch-fulfillment-won-modal_header">
                    <h2 className="merch-fulfillment-won-modal_header_headline">
                        YOU&apos;VE BEEN SELECTED
                    </h2>
                    <p className="merch-fulfillment-won-modal_header_subhead">
                        VERIFY YOUR ELIGIBILITY TO CLAIM
                    </p>
                    <p className="merch-fulfillment-won-modal_header_prize-name">
                        {rewardName}
                    </p>
                </div>
                {assetId &&
                    <div className="merch-fulfillment-won-modal_body_image">
                        <AssetImage assetId={assetId} alt={rewardName} />
                    </div>
                }
                <RewardFulfillmentClaimForm
                    distributedRewardId={reward.id}
                    rewardName={rewardName}
                    onSubmitted={onClaimed}
                    onOfficialRulesClick={() => setRulesOpen(true)}
                />
            </ModalBody>
            <MinelabRttrRulesModal
                isOpen={rulesOpen}
                onClose={() => setRulesOpen(false)}
            />
        </Modal>
    );
};

export default MerchFulfillmentWonModal;
